import { isEmpty } from 'lodash-es'
import { ComponentProps, useContext, useEffect, useRef } from 'react'
import { LuMapPin, LuSearch, LuX } from 'react-icons/lu'

import {
  SearchItem,
  SearchResult,
  useAutoCompleteLocationSearch,
} from '@/feature/map/hooks/useAutoCompleteLocationSearch'
import { MapContext } from '@/feature/map/context/map'

import styles from './MapSearchBar.module.css'

export type MapSearchBarProps = {
  onSearchChanged?: (search: SearchItem | undefined) => void
  searchInfo?: SearchItem
} & Omit<ComponentProps<'label'>, 'children'>

const SearchResults = <T extends SearchResult>({
  results,
  onPlaceSelected,
  isLoading,
  highlightedIndex,
}: {
  results: T[] | undefined
  onPlaceSelected: (place: T) => void
  isLoading: boolean
  highlightedIndex?: number
}) => {
  const activeElement = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (activeElement.current) {
      activeElement.current.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  if (results == null) {
    return null
  } else if (!results.length) {
    return <div className={styles.noSearchItem}>No results found</div>
  } else {
    return (
      <ul>
        {results.map((result, i) => {
          const refWorkaround = i === highlightedIndex ? { ref: activeElement } : {}
          return (
            <li
              key={result.id}
              className={[
                styles.searchItem,
                i === highlightedIndex ? styles.highlighted : null,
              ].join(' ')}
              {...refWorkaround}
            >
              <button type="button" onClick={() => onPlaceSelected(result)}>
                <div className={styles.searchResultDisplay}>
                  <div className={styles.searchResultDisplayIcon}>
                    <LuMapPin color={'#404040'} size={16} strokeWidth={1.5} />
                  </div>
                  <div className={styles.searchResultDisplayText}>{result.address}</div>
                </div>
              </button>
            </li>
          )
        })}

        {isLoading ? <li className={styles.isSearching}>Searching...</li> : null}
      </ul>
    )
  }
}

export const MapSearchBar = ({
  className,
  searchInfo: _searchInfo,
  onSearchChanged,
  ...props
}: MapSearchBarProps) => {
  const searchBoxElement = useRef<HTMLInputElement>(null)

  const map = useContext(MapContext)

  const {
    search,
    isSearching,
    searchResults,
    forceCloseResults,
    highlightedIndex,
    geoPoint,
    locations,
    setSearch,
    resolveSearch,
    onPlaceSelected,
  } = useAutoCompleteLocationSearch({
    map,
    searchBoxElement,
    onSearchChanged,
  })

  return (
    <>
      <label {...props} className={[className, styles.searchContainer].join(' ')}>
        <div className={styles.searchIcon}>
          <LuSearch size="24" strokeWidth={1.5} />
          <div className={styles.noOverflow}>
            <button
              type="button"
              onClick={() => setSearch('')}
              className={[styles.clearButton, isEmpty(search) ? styles.empty : ''].join(' ')}
            >
              <LuX size="24" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <input
          type="text"
          placeholder="Search"
          ref={searchBoxElement}
          className={styles.searchInput}
          value={search}
          onKeyUp={resolveSearch}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div
          className={[styles.searchResults, forceCloseResults ? styles.forceClose : null].join(' ')}
        >
          <SearchResults
            results={searchResults === geoPoint ? undefined : locations}
            isLoading={isSearching}
            highlightedIndex={highlightedIndex}
            onPlaceSelected={onPlaceSelected}
          />
        </div>
      </label>
    </>
  )
}
